import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, AlertTriangle, Check, X, DollarSign, Zap, Shield, Flame, Star } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const STAT_ICONS = {
  cash: { icon: <DollarSign size={14} />, color: '#C9A84C', label: 'CASH' },
  xp: { icon: <Star size={14} />, color: '#9F7AEA', label: 'XP' },
  heat: { icon: <Flame size={14} />, color: '#DC2626', label: 'HEAT' },
  respect: { icon: <Shield size={14} />, color: '#10B981', label: 'RESPECT' },
  energy: { icon: <Zap size={14} />, color: '#3B82F6', label: 'ENERGY' },
};

const InteractiveMission = ({ gameId, jobId, onComplete, onCancel }) => {
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState('intro'); // intro, decision_0, decision_1, decision_2, executing, result
  const [decisions, setDecisions] = useState([]);
  const [currentDecision, setCurrentDecision] = useState(0);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);

  const loadMission = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/game/${gameId}/mission/${jobId}`);
      if (res.ok) {
        const d = await res.json();
        if (d.interactive) setMission(d);
        else { onCancel?.(); return; }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [gameId, jobId, onCancel]);

  useEffect(() => { loadMission(); }, [loadMission]);

  const handleChoice = (decisionId, choiceId) => {
    const newDecisions = [...decisions, { id: decisionId, choice: choiceId }];
    setDecisions(newDecisions);

    if (currentDecision < (mission?.decisions?.length || 0) - 1) {
      setCurrentDecision(prev => prev + 1);
      setPhase(`decision_${currentDecision + 1}`);
    } else {
      setPhase('executing');
      executeMission(newDecisions);
    }
  };

  const executeMission = async (allDecisions) => {
    setExecuting(true);
    try {
      const res = await fetch(`${API}/api/game/${gameId}/mission/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: jobId, decisions: allDecisions }),
      });
      if (res.ok) {
        const d = await res.json();
        // Brief delay for dramatic effect
        await new Promise(r => setTimeout(r, 1500));
        setResult(d);
        setPhase('result');
      }
    } catch (e) { console.error(e); }
    setExecuting(false);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!mission) return null;

  const det = result?.job_result?.details || {};

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4 overflow-y-auto"
      data-testid="interactive-mission"
    >
      <div className="max-w-lg w-full">
        <AnimatePresence mode="wait">
          {/* INTRO */}
          {phase === 'intro' && (
            <motion.div key="intro" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6 text-center">
              <div>
                <p className="font-['Outfit'] text-[#C9A84C] text-[9px] tracking-[0.3em] mb-2">ERA 1 — THE FOUNDATION</p>
                <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-[#E8E0D0]" data-testid="mission-title">{mission.name}</h2>
              </div>
              <p className="font-['Outfit'] text-[#E8E0D0]/60 text-sm leading-relaxed max-w-md mx-auto">{mission.intro}</p>
              <div className="flex gap-3 justify-center">
                <button onClick={onCancel} className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] tracking-widest px-4 py-2 border border-[#1A2535] rounded hover:border-[#E8E0D0]/20 transition-colors" data-testid="mission-cancel">BACK</button>
                <button onClick={() => { setPhase('decision_0'); setCurrentDecision(0); }}
                  className="font-['Outfit'] text-[#C9A84C] text-[10px] tracking-widest px-6 py-2 border border-[#C9A84C]/40 rounded bg-[#C9A84C]/10 hover:bg-[#C9A84C]/20 transition-colors flex items-center gap-2"
                  data-testid="mission-begin">
                  BEGIN MISSION <ChevronRight size={14} />
                </button>
              </div>
            </motion.div>
          )}

          {/* DECISIONS */}
          {phase.startsWith('decision_') && mission.decisions[currentDecision] && (
            <motion.div key={`dec-${currentDecision}`} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} className="space-y-5">
              <div className="flex items-center justify-between">
                <p className="font-['Outfit'] text-[#C9A84C]/40 text-[8px] tracking-widest">DECISION {currentDecision + 1} OF {mission.decisions.length}</p>
                <div className="flex gap-1">
                  {mission.decisions.map((_, i) => (
                    <div key={i} className={`w-8 h-1 rounded-full ${i <= currentDecision ? 'bg-[#C9A84C]' : 'bg-[#1A2535]'}`} />
                  ))}
                </div>
              </div>

              <h3 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#E8E0D0]" data-testid="decision-prompt">
                {mission.decisions[currentDecision].prompt}
              </h3>

              <div className="space-y-2.5">
                {mission.decisions[currentDecision].options.map(opt => (
                  <motion.button
                    key={opt.id}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => opt.available !== false && handleChoice(mission.decisions[currentDecision].id, opt.id)}
                    disabled={opt.available === false}
                    className={`w-full text-left p-4 rounded-lg border transition-all ${
                      opt.available === false
                        ? 'border-[#1A2535] opacity-30 cursor-not-allowed'
                        : 'border-[#1A2535] hover:border-[#C9A84C]/40 hover:bg-[#C9A84C]/5 cursor-pointer'
                    }`}
                    data-testid={`choice-${opt.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-['Outfit'] text-[#E8E0D0] text-xs font-bold tracking-wider">{opt.label}</p>
                        <p className="font-['Outfit'] text-[#E8E0D0]/40 text-[10px] mt-1">{opt.desc}</p>
                        {opt.unavailable_reason && <p className="font-['Outfit'] text-[#DC2626] text-[9px] mt-1">{opt.unavailable_reason}</p>}
                      </div>
                      <ChevronRight size={16} className="text-[#E8E0D0]/10 mt-1 flex-shrink-0" />
                    </div>
                    {/* Modifier hints */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {opt.modifiers?.heat_mod > 0 && <span className="font-['Outfit'] text-[8px] px-1.5 py-0.5 rounded bg-[#DC2626]/10 text-[#DC2626]">+{opt.modifiers.heat_mod} HEAT</span>}
                      {opt.modifiers?.heat_mod < 0 && <span className="font-['Outfit'] text-[8px] px-1.5 py-0.5 rounded bg-[#10B981]/10 text-[#10B981]">{opt.modifiers.heat_mod} HEAT</span>}
                      {opt.modifiers?.respect_mod > 0 && <span className="font-['Outfit'] text-[8px] px-1.5 py-0.5 rounded bg-[#10B981]/10 text-[#10B981]">+{opt.modifiers.respect_mod} RESPECT</span>}
                      {opt.modifiers?.respect_mod < 0 && <span className="font-['Outfit'] text-[8px] px-1.5 py-0.5 rounded bg-[#DC2626]/10 text-[#DC2626]">{opt.modifiers.respect_mod} RESPECT</span>}
                      {opt.modifiers?.cash_mod < 0 && <span className="font-['Outfit'] text-[8px] px-1.5 py-0.5 rounded bg-[#F59E0B]/10 text-[#F59E0B]">${opt.modifiers.cash_mod}</span>}
                      {opt.modifiers?.cash_mod > 0 && <span className="font-['Outfit'] text-[8px] px-1.5 py-0.5 rounded bg-[#C9A84C]/10 text-[#C9A84C]">+${opt.modifiers.cash_mod}</span>}
                      {opt.modifiers?.success_bonus > 0 && <span className="font-['Outfit'] text-[8px] px-1.5 py-0.5 rounded bg-[#3B82F6]/10 text-[#3B82F6]">+{opt.modifiers.success_bonus}% SUCCESS</span>}
                      {opt.modifiers?.success_bonus < 0 && <span className="font-['Outfit'] text-[8px] px-1.5 py-0.5 rounded bg-[#DC2626]/10 text-[#DC2626]">{opt.modifiers.success_bonus}% SUCCESS</span>}
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* EXECUTING */}
          {phase === 'executing' && (
            <motion.div key="exec" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center space-y-6 py-10">
              <div className="w-12 h-12 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin mx-auto" />
              <div>
                <p className="font-['Cormorant_Garamond'] text-xl text-[#E8E0D0]">IN MOTION...</p>
                <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] tracking-widest mt-2">THE STREETS ARE WATCHING</p>
              </div>
            </motion.div>
          )}

          {/* RESULT */}
          {phase === 'result' && result && (
            <motion.div key="result" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="space-y-5">
              <div className="text-center">
                {result.job_result?.success ? (
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', bounce: 0.5 }}>
                    <Check size={48} className="text-[#10B981] mx-auto mb-3" />
                  </motion.div>
                ) : (
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', bounce: 0.5 }}>
                    <X size={48} className="text-[#DC2626] mx-auto mb-3" />
                  </motion.div>
                )}
                <h3 className="font-['Cormorant_Garamond'] text-xl font-bold" style={{ color: result.job_result?.success ? '#10B981' : '#DC2626' }}>
                  {result.job_result?.success ? 'MISSION COMPLETE' : 'MISSION FAILED'}
                </h3>
                <p className="font-['Outfit'] text-[#E8E0D0]/40 text-sm mt-2">{result.job_result?.narrator}</p>
              </div>

              {/* Stat changes */}
              <div className="grid grid-cols-2 gap-2" data-testid="mission-stat-changes">
                {[
                  { key: 'cash_change', ...STAT_ICONS.cash },
                  { key: 'xp_change', ...STAT_ICONS.xp },
                  { key: 'heat_change', ...STAT_ICONS.heat },
                  { key: 'respect_change', ...STAT_ICONS.respect },
                ].map(s => {
                  const val = det[s.key] || 0;
                  if (val === 0) return null;
                  const positive = val > 0;
                  return (
                    <motion.div key={s.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                      className="flex items-center gap-2 p-2.5 rounded-lg border"
                      style={{ borderColor: s.color + '30', backgroundColor: s.color + '08' }}>
                      <span style={{ color: s.color }}>{s.icon}</span>
                      <div>
                        <p className="font-['Outfit'] text-[8px] tracking-wider" style={{ color: s.color + '80' }}>{s.label}</p>
                        <p className="font-['Outfit'] text-sm font-bold" style={{ color: positive ? '#10B981' : '#DC2626' }}>
                          {positive ? '+' : ''}{s.key === 'cash_change' ? `$${val.toLocaleString()}` : val}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Roll info */}
              <div className="text-center">
                <p className="font-['Outfit'] text-[#E8E0D0]/15 text-[8px] tracking-widest">
                  ROLL: {det.roll} / {det.chance}% CHANCE
                </p>
              </div>

              <button onClick={() => onComplete?.(result)}
                className="w-full py-3 bg-[#C9A84C]/10 border border-[#C9A84C]/40 rounded-lg text-[#C9A84C] font-['Outfit'] text-xs tracking-widest hover:bg-[#C9A84C]/20 transition-colors"
                data-testid="mission-continue">
                CONTINUE
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default InteractiveMission;
